import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../services/axios";
import { getCurrentClientId } from "../../config/villageConfig";
import DownloadCard from "../DownloadCard";
import formIcon from "../../assets/icons/form.png";
import IncomeSource from "./IncomeSource";
import Expenditure from "./Expenditure";
import BudgetCard from "../BudgetCard";

// Format number to Indian comma-separated format (e.g., 1,00,000)
const formatIndianNumber = (num) => {
  if (num == null || num === "" || num === "NA") return num;
  const numStr = String(num).replace(/,/g, ''); // Remove existing commas
  const number = parseFloat(numStr);
  if (isNaN(number)) return num;

  // Use Indian locale for formatting
  return number.toLocaleString('en-IN');
};

const toDevanagari = (n) => {
  if (n == null || n === "" || n === "NA") return n;
  // First format with Indian commas, then convert to Devanagari
  const formatted = formatIndianNumber(n);
  const map = { "0": "०", "1": "१", "2": "२", "3": "३", "4": "४", "5": "५", "6": "६", "7": "७", "8": "८", "9": "९" };
  return String(formatted).replace(/[0-9]/g, (d) => map[d] || d);
};

const COLORS = [
  "bg-[linear-gradient(135deg,#2B7FFF_0%,#002371_100%)]",
  "bg-[linear-gradient(135deg,#00C950_0%,#006D29_100%)]",
  "bg-[linear-gradient(135deg,#EC5042_0%,#B7131A_100%)]",
  "bg-[linear-gradient(135deg,#00B8DB_0%,#004A5D_100%)]",
];

// Client-specific budget data (only for clientId 1)
const CLIENT_1_BUDGET_DATA = {
  total_budget: "NA",
  total_income: "8350481",
  total_expense: "6497986",
  balance: "1852495"
};

const DEFAULT_BUDGET_MR = [
  { title: "एकूण बजेट", value: "₹०", label: "एकूण बजेट", color: COLORS[0] },
  { title: "उत्पन्न", value: "₹०", label: "उत्पन्न", color: COLORS[1] },
  { title: "खर्च", value: "₹०", label: "खर्च", color: COLORS[2] },
  { title: "शिल्लक", value: "₹०", label: "उर्वरित निधी", color: COLORS[3] },
];

const IncomeAndExpenditure = ({ financialYear }) => {
  const { t, i18n } = useTranslation();
  const clientId = getCurrentClientId();
  const lang = i18n.language;

  // Initialize with client-specific data for clientId 1
  const getInitialBudgetData = () => {
    if (clientId === 1) {
      const b = CLIENT_1_BUDGET_DATA;

      if (lang === "mr") {
        return [
          { title: "एकूण बजेट", value: `₹${toDevanagari(b.total_budget)}`, label: "एकूण बजेट", color: COLORS[0] },
          { title: "उत्पन्न", value: `₹${toDevanagari(b.total_income)}`, label: "उत्पन्न", color: COLORS[1] },
          { title: "खर्च", value: `₹${toDevanagari(b.total_expense)}`, label: "खर्च", color: COLORS[2] },
          { title: "शिल्लक", value: `₹${toDevanagari(b.balance)}`, label: "उर्वरित निधी", color: COLORS[3] },
        ];
      }
      return [
        { title: "Total Budget", value: `₹${formatIndianNumber(b.total_budget)}`, label: "Total Budget", color: COLORS[0] },
        { title: "Income", value: `₹${formatIndianNumber(b.total_income)}`, label: "Income", color: COLORS[1] },
        { title: "Expenditure", value: `₹${formatIndianNumber(b.total_expense)}`, label: "Expenditure", color: COLORS[2] },
        { title: "Balance", value: `₹${formatIndianNumber(b.balance)}`, label: "Remaining Funds", color: COLORS[3] },
      ];
    }
    return DEFAULT_BUDGET_MR;
  };

  const [budgetData, setBudgetData] = useState(getInitialBudgetData());
  const [incomeData, setIncomeData] = useState(null);
  const [totalIncome, setTotalIncome] = useState(clientId === 1 ? parseFloat(CLIENT_1_BUDGET_DATA.total_income) : 0);
  const [totalExpense, setTotalExpense] = useState(clientId === 1 ? parseFloat(CLIENT_1_BUDGET_DATA.total_expense) : 0);
  const [expenditureData, setExpenditureData] = useState(null);
  const [downloadLink, setDownloadLink] = useState("");

  useEffect(() => {
    let mounted = true;

    const buildBudgetData = (b) => {
      const total = b?.total_budget || "0";
      const income = b?.total_income || "0";
      const expense = b?.total_expense || "0";
      const balance = b?.balance || "0";

      // Set total income and expense for props
      setTotalIncome(parseFloat(income) || 0);
      setTotalExpense(parseFloat(expense) || 0);

      if (lang === "mr") {
        return [
          { title: "एकूण बजेट", value: `₹${toDevanagari(total)}`, label: "एकूण बजेट", color: COLORS[0] },
          { title: "उत्पन्न", value: `₹${toDevanagari(income)}`, label: "उत्पन्न", color: COLORS[1] },
          { title: "खर्च", value: `₹${toDevanagari(expense)}`, label: "खर्च", color: COLORS[2] },
          { title: "शिल्लक", value: `₹${toDevanagari(balance)}`, label: "उर्वरित निधी", color: COLORS[3] },
        ];
      }
      return [
        { title: "Total Budget", value: `₹${formatIndianNumber(total)}`, label: "Total Budget", color: COLORS[0] },
        { title: "Income", value: `₹${formatIndianNumber(income)}`, label: "Income", color: COLORS[1] },
        { title: "Expenditure", value: `₹${formatIndianNumber(expense)}`, label: "Expenditure", color: COLORS[2] },
        { title: "Balance", value: `₹${formatIndianNumber(balance)}`, label: "Remaining Funds", color: COLORS[3] },
      ];
    };

    const fetchBudgets = async () => {
      // Hardcode data for clientId 1, skip API call
      if (clientId === 1) {
        if (!mounted) return;
        setBudgetData(buildBudgetData(CLIENT_1_BUDGET_DATA));
        return;
      }

      try {
        const res = await axiosInstance.get("/budgets");
        if (!mounted) return;
        const data = res.data || [];
        if (Array.isArray(data) && data.length > 0) {
          // Filter by financial year ID if provided
          const yearId = financialYear?.id;
          const filtered = yearId ? data.filter((b) => b.year === yearId) : data;
          const budget = filtered.length > 0 ? filtered[0] : data[0];
          setBudgetData(buildBudgetData(budget));
        } else {
          setBudgetData(DEFAULT_BUDGET_MR);
        }
      } catch (err) {
        console.error("Failed to load budgets:", err);
        setBudgetData(DEFAULT_BUDGET_MR);
      }
    };

    const fetchIncome = async () => {
      // Hardcode data for clientId 1, skip API call
      if (clientId === 1) {
        if (!mounted) return;
        // Data is already set via IncomeSource default props
        return;
      }

      try {
        const res = await axiosInstance.get("/financial-reports", {
          params: {
            tag: "Income",
            financial_year: financialYear?.id,
          },
        });
        if (!mounted) return;
        const data = res.data || [];
        data.sort((a, b) => a.id - b.id);
        setIncomeData(data.length > 0 ? data : null);
      } catch (err) {
        console.error("Failed to load budgets:", err);
        setIncomeData(null);
      }
    };

    const fetchExpenditure = async () => {
      // Hardcode data for clientId 1, skip API call
      if (clientId === 1) {
        if (!mounted) return;
        // Data is already set via Expenditure default props
        return;
      }

      try {
        const res = await axiosInstance.get("/financial-reports", {
          params: {
            tag: "Expense",
            financial_year: financialYear?.id,
          },
        });
        if (!mounted) return;
        const data = res.data || [];
        data.sort((a, b) => a.id - b.id);
        if (Array.isArray(data) && data.length > 0) {
          setExpenditureData(data.length > 0 ? data : null);
        }
      } catch (err) {
        console.error("Failed to load budgets:", err);
        setExpenditureData(null)
      }
    };

    const fetchDocument = async () => {
      // Skip for clientId 1 for now (can fetch documents later if needed)
      if (clientId === 1) {
        return;
      }

      try {
        const res = await axiosInstance.get("/financial-report-docs", {
          params: {
            tag: "Financial Report",
            financial_year: financialYear?.id,
          },
        });
        if (!mounted) return;
        const data = res.data || [];
        if (Array.isArray(data) && data.length > 0) {
          // Get the first document's file URL
          const doc = data[0];
          const newLink = doc.pdf_file || "";
          setDownloadLink(newLink);
          console.log("Updated download link:", newLink);
        } else {
          setDownloadLink("");
        }
      } catch (err) {
        console.error("Failed to load financial documents:", err);
        setDownloadLink("");
      }
    };

    fetchBudgets();
    fetchIncome();
    fetchExpenditure();
    fetchDocument();
    return () => { mounted = false; };
  }, [lang, financialYear]);

  return (
    <div className="border rounded-md p-8 bg-white flex flex-col gap-2">
      <div className="flex flex-col gap-3">
        {/* Header */}
        <div className="flex gap-3 items-center">
          <img className="w-10 h-10 relative" src={formIcon} alt="" />
          <h2 className="text-2xl max-sm:text-xl m-0">
            {t("incomeExpenditure.title") || "बजेट सारांश"}
          </h2>
        </div>

        <p className="font-normal text-base m-0">
          {t("incomeExpenditure.description") || "आर्थिक वर्षाचा बजेट सारांश आमच्या खर्च आणि कमाईचे तपशील प्रदान करतो."}
        </p>

        {/* Budget Summary Cards */}
        <h1 className="text-2xl max-sm:text-xl m-0">
          {t("budgetSummary.title") || "बजेट सारांश"}
        </h1>
        <div className="grid grid-cols-4 max-lg:grid-cols-2 max-sm:grid-cols-1 gap-4 w-full">
          {budgetData.map((item, idx) => (
            <BudgetCard key={idx} {...item} />
          ))}
        </div>

        {/* Detailed Information */}
        <h1 className="text-2xl max-sm:text-xl m-0 mt-3">
          {t("detailedInformation.title") || "तपशीलवार माहिती"}
        </h1>

        <IncomeSource data={incomeData} totalIncome={totalIncome} selectedYear={financialYear} />
        <Expenditure data={expenditureData} totalExpense={totalExpense} selectedYear={financialYear} />

        <DownloadCard
          key={downloadLink || 'no-link'}
          title={t("incomeExpenditure.reportTitle") || "अहवाल"}
          titleEn="Report"
          description={t("incomeExpenditure.reportDescription") || "तुमच्या निवासाची माहिती आणि संबंधित तपशील नोंदवणारा रहिवासी स्व-घोषणा फॉर्म डाउनलोड करा."}
          descriptionEn="Download the income and expenditure report."
          downloadLink={downloadLink}
          buttonLabel="डाउनलोड करा"
          buttonLabelEn="Download"
          isExternalLink={false}
        />
      </div>
    </div>
  );
};

export default IncomeAndExpenditure;
